import { DEFAULT_REGION } from '@/configs/env/public';
import { Country } from '@/constructs/Country';
import { Language } from '@/constructs/Language';
import type { ILocale } from '@/constructs/LocaleSchema';
import type { LocalizationStringMap, LocalizationStringPath } from '@/lang';
import type { Primitive } from '@/type-utils';
import StaleWhileRevalidate from '@/utils/StaleWhileRevalidate';
import type { InvalidArgumentError } from '@/utils/errors';
import { exhaustiveGuard } from '@/utils/function-utils';
import type { JSX, ReactElement } from 'react';
import I18NService from './I18NService';
import { country_countries_AD } from "@/lang/__generated__/ahnu/country_countries_AD";
import { country_countries_AL } from "@/lang/__generated__/ahnu/country_countries_AL";
import { country_countries_AT } from "@/lang/__generated__/ahnu/country_countries_AT";
import { country_countries_AU } from "@/lang/__generated__/ahnu/country_countries_AU";
import { country_countries_AE } from "@/lang/__generated__/ahnu/country_countries_AE";
import { country_countries_BA } from "@/lang/__generated__/ahnu/country_countries_BA";
import { country_countries_BE } from "@/lang/__generated__/ahnu/country_countries_BE";
import { country_countries_BG } from "@/lang/__generated__/ahnu/country_countries_BG";
import { country_countries_BR } from "@/lang/__generated__/ahnu/country_countries_BR";
import { country_countries_CA } from "@/lang/__generated__/ahnu/country_countries_CA";
import { country_countries_CH } from "@/lang/__generated__/ahnu/country_countries_CH";
import { country_countries_CL } from "@/lang/__generated__/ahnu/country_countries_CL";
import { country_countries_CN } from "@/lang/__generated__/ahnu/country_countries_CN";
import { country_countries_CO } from "@/lang/__generated__/ahnu/country_countries_CO";
import { country_countries_CY } from "@/lang/__generated__/ahnu/country_countries_CY";
import { country_countries_CZ } from "@/lang/__generated__/ahnu/country_countries_CZ";
import { country_countries_DE } from "@/lang/__generated__/ahnu/country_countries_DE";
import { country_countries_DK } from "@/lang/__generated__/ahnu/country_countries_DK";
import { country_countries_DZ } from "@/lang/__generated__/ahnu/country_countries_DZ";
import { country_countries_EE } from "@/lang/__generated__/ahnu/country_countries_EE";
import { country_countries_ES } from "@/lang/__generated__/ahnu/country_countries_ES";
import { country_countries_FI } from "@/lang/__generated__/ahnu/country_countries_FI";
import { country_countries_FR } from "@/lang/__generated__/ahnu/country_countries_FR";
import { country_countries_UK } from "@/lang/__generated__/ahnu/country_countries_UK";
import { country_countries_GR } from "@/lang/__generated__/ahnu/country_countries_GR";
import { country_countries_HK } from "@/lang/__generated__/ahnu/country_countries_HK";
import { country_countries_HR } from "@/lang/__generated__/ahnu/country_countries_HR";
import { country_countries_HU } from "@/lang/__generated__/ahnu/country_countries_HU";
import { country_countries_ID } from "@/lang/__generated__/ahnu/country_countries_ID";
import { country_countries_IE } from "@/lang/__generated__/ahnu/country_countries_IE";
import { country_countries_IL } from "@/lang/__generated__/ahnu/country_countries_IL";
import { country_countries_IS } from "@/lang/__generated__/ahnu/country_countries_IS";
import { country_countries_IT } from "@/lang/__generated__/ahnu/country_countries_IT";
import { country_countries_JE } from "@/lang/__generated__/ahnu/country_countries_JE";
import { country_countries_JP } from "@/lang/__generated__/ahnu/country_countries_JP";
import { country_countries_KR } from "@/lang/__generated__/ahnu/country_countries_KR";
import { country_countries_KZ } from "@/lang/__generated__/ahnu/country_countries_KZ";
import { country_countries_LT } from "@/lang/__generated__/ahnu/country_countries_LT";
import { country_countries_LU } from "@/lang/__generated__/ahnu/country_countries_LU";
import { country_countries_LV } from "@/lang/__generated__/ahnu/country_countries_LV";
import { country_countries_MA } from "@/lang/__generated__/ahnu/country_countries_MA";
import { country_countries_MC } from "@/lang/__generated__/ahnu/country_countries_MC";
import { country_countries_MD } from "@/lang/__generated__/ahnu/country_countries_MD";
import { country_countries_ME } from "@/lang/__generated__/ahnu/country_countries_ME";
import { country_countries_MK } from "@/lang/__generated__/ahnu/country_countries_MK";
import { country_countries_MT } from "@/lang/__generated__/ahnu/country_countries_MT";
import { country_countries_NL } from "@/lang/__generated__/ahnu/country_countries_NL";
import { country_countries_NO } from "@/lang/__generated__/ahnu/country_countries_NO";
import { country_countries_NZ } from "@/lang/__generated__/ahnu/country_countries_NZ";
import { country_countries_PH } from "@/lang/__generated__/ahnu/country_countries_PH";
import { country_countries_PL } from "@/lang/__generated__/ahnu/country_countries_PL";
import { country_countries_PT } from "@/lang/__generated__/ahnu/country_countries_PT";
import { country_countries_RE } from "@/lang/__generated__/ahnu/country_countries_RE";
import { country_countries_RO } from "@/lang/__generated__/ahnu/country_countries_RO";
import { country_countries_RS } from "@/lang/__generated__/ahnu/country_countries_RS";
import { country_countries_RU } from "@/lang/__generated__/ahnu/country_countries_RU";
import { country_countries_SE } from "@/lang/__generated__/ahnu/country_countries_SE";
import { country_countries_SG } from "@/lang/__generated__/ahnu/country_countries_SG";
import { country_countries_SI } from "@/lang/__generated__/ahnu/country_countries_SI";
import { country_countries_SK } from "@/lang/__generated__/ahnu/country_countries_SK";
import { country_countries_TN } from "@/lang/__generated__/ahnu/country_countries_TN";
import { country_countries_TR } from "@/lang/__generated__/ahnu/country_countries_TR";
import { country_countries_TW } from "@/lang/__generated__/ahnu/country_countries_TW";
import { country_countries_US } from "@/lang/__generated__/ahnu/country_countries_US";
import { country_countries_ZA } from "@/lang/__generated__/ahnu/country_countries_ZA";
import { language_en } from "@/lang/__generated__/ahnu/language_en";
import { language_es } from "@/lang/__generated__/ahnu/language_es";
import { language_fr } from "@/lang/__generated__/ahnu/language_fr";
import { language_de } from "@/lang/__generated__/ahnu/language_de";
import { language_it } from "@/lang/__generated__/ahnu/language_it";
import { language_jp } from "@/lang/__generated__/ahnu/language_jp";
import { language_nl } from "@/lang/__generated__/ahnu/language_nl";

const regionConfig = {
  US: {
    supportedCurrencies: ['USD'],
    defaultCurrency: 'USD',
    locales: ['en', 'en-US'],
    defaultLocale: 'en'
  }
} as const;

export const i18nConfig = {
  defaultRegion: DEFAULT_REGION,
  regionConfig
} as const;

export { Country } from '@/constructs/Country';
export { Language } from '@/constructs/Language';

export * from '@/constructs/LocaleSchema';
export * from '@/lang/types/Resource';

export default I18NService;

/**
 * Retrieves a localized resource string for the given identifier and locale (or fallbacks
 * to the "current" locale if none is provided). The resource string must be defined in the
 * `lang` directory.
 *
 * If the resource string contains placeholders, use {@link msgf} instead.
 *
 * @param msgID - The identifier for the resource string. It must be in the form of
 * `[file].[key].[...etc]`, where `file` is the name of the file in the `lang`,
 * `key` is the key in the exported object, and `...etc` is any sequence of nested keys
 * which resolve to a resource string.
 * @param [locale] - The lang-locale of the given message.
 * @returns The localized resource string.
 *
 * @example
 * msg('general.hello') // => 'Hello, World!'
 * msg('general.hello', JP_LOCALE) // => 'こんにちは、世界！'
 */
export function msg<Path extends LocalizationStringPath>(
  msgID: Path,
  locale?: ILocale
): LocalizationStringMap[Path] {
  return I18NService.msg(msgID, locale);
}

/**
 * Retrieves a configurable, localized resource string for the given identifier and locale
 * (or fallbacks to the "current" locale if none is provided). The resource string must be
 * defined in the `lang` directory.
 *
 * The resource string should contain placeholders in ICU MessageFormat syntax, which can be
 * interpolated with the values provided in the `values` parameter. If the resource string
 * does not contain placeholders, use {@link msg} instead.
 *
 * @param msgID - The identifier for the resource string. It must be in the form of
 * `[file].[key].[...etc]`, where `file` is the name of the file in the `lang`,
 * `key` is the key in the exported object, and `...etc` is any sequence of nested keys
 * which resolve to a resource string.
 * @param values - A record of values to be interpolated into the string. The values
 * provided should match the placeholders in the resource string.
 * @param [locale] - The lang-locale of the given message.
 * @returns The localized resource string formatted with the passed in values in
 * accordance with the ICU MessageFormat specification.
 *
 * **Note:** The return value is not guaranteed to a string. It could be a React element
 * if the resource string is formatted with JSX. The type displayed in the IDE is just
 * there for conveniently inspecting the placeholder values.
 *
 * @see {@link https://formatjs.github.io/docs/core-concepts/icu-syntax/ ICU Message Syntax}
 * @see {@link https://formatjs.github.io/docs/intl-messageformat/#common-usage-example Common Usage Example}
 *
 * @example
 * // 'cart.quantity' => 'You have {quantity, plural, one {1 item} other {# items}} in your cart.'
 * msgf('cart.quantity', { quantity: 1 }) // => 'You have 1 item in your cart.'
 * msgf('cart.quantity', { quantity: 5 }) // => 'You have 5 items in your cart.'
 */
export function msgf<Path extends LocalizationStringPath>(
  msgID: Path,
  values: Record<string, Primitive | ReactElement<unknown> | JSX.Element>,
  locale?: ILocale
): LocalizationStringMap[Path] {
  return I18NService.msgf(msgID, values, locale);
}

/**
 * @deprecated Use `msg` instead for better performance and type safety.
 *
 * Dynamically retrieves a localized resource string for the given identifier in the
 * "current" locale. The resource string must be defined in the `lang` directory.
 * The difference between this method and {@link msg} is that this method will return
 * a {@link StaleWhileRevalidate} object that can be used to asynchronously load and display
 * the resource string.
 *
 * **Note:** This method is _not_ type safe. It is recommended to use `msg`
 * instead for type safety. Furthermore, this method should only be used when the
 * resource ID is not known at compile time. The method necessarily requires that all
 * resource strings be loaded at runtime, which can have a sizeable impact on the
 * perceived performance of the application.
 *
 * **Note 2:** There is no dynamic version of `msgf` because if the resource ID is not
 * known at compile time, then it's unlikely that the values to be interpolated are known
 * either.
 *
 * @param msgID - The identifier for the resource string. It must be in the form of
 * `[file].[key].[...etc]`, where `file` is the name of the file in the `lang`,
 * `key` is the key in the exported object, and `...etc` is any sequence of nested keys
 * which resolve to a resource string.
 * @returns The localized resource string.
 */
export const msgDynamic = (msgID: string): StaleWhileRevalidate<string> => {
  return I18NService.msgDynamic(msgID);
};

/**
 * Takes a date and formats it based on the current locale and options provided.
 * @param date - The date to format.
 * @param options - Options to pass to the formatter.
 * @returns The date formatted.
 */
export const formatDate = (
  date: Date,
  options: Intl.DateTimeFormatOptions
): string => {
  return I18NService.formatDate(date, options);
};

/**
 * Retrieves the currency symbol based in current locale.
 * @returns - The current currency symbol.
 */
export const currentCurrencySymbol = (): string => {
  return I18NService.currentCurrencySymbol();
};

/**
 * Retrieves the display name for the specified country.
 *
 * **NOTE:** Names must be configured via the `countries` lang file.
 *
 * @param country - The country to get the name for.
 * @returns The specified country's display name.
 *
 * @throws An {@link InvalidArgumentError} if the specified country does not
 * have a configured display name.
 */
export const getCountryName = (country: Country): string => {
  switch (country) {
    case Country.AD:
      return msg(country_countries_AD);
    case Country.AL:
      return msg(country_countries_AL);
    case Country.AT:
      return msg(country_countries_AT);
    case Country.AU:
      return msg(country_countries_AU);
    case Country.AE:
      return msg(country_countries_AE);
    case Country.BA:
      return msg(country_countries_BA);
    case Country.BE:
      return msg(country_countries_BE);
    case Country.BG:
      return msg(country_countries_BG);
    case Country.BR:
      return msg(country_countries_BR);
    case Country.CA:
      return msg(country_countries_CA);
    case Country.CH:
      return msg(country_countries_CH);
    case Country.CL:
      return msg(country_countries_CL);
    case Country.CN:
      return msg(country_countries_CN);
    case Country.CO:
      return msg(country_countries_CO);
    case Country.CY:
      return msg(country_countries_CY);
    case Country.CZ:
      return msg(country_countries_CZ);
    case Country.DE:
      return msg(country_countries_DE);
    case Country.DK:
      return msg(country_countries_DK);
    case Country.DZ:
      return msg(country_countries_DZ);
    case Country.EE:
      return msg(country_countries_EE);
    case Country.ES:
      return msg(country_countries_ES);
    case Country.FI:
      return msg(country_countries_FI);
    case Country.FR:
      return msg(country_countries_FR);
    case Country.GB:
      return msg(country_countries_UK);
    case Country.GR:
      return msg(country_countries_GR);
    case Country.HK:
      return msg(country_countries_HK);
    case Country.HR:
      return msg(country_countries_HR);
    case Country.HU:
      return msg(country_countries_HU);
    case Country.ID:
      return msg(country_countries_ID);
    case Country.IE:
      return msg(country_countries_IE);
    case Country.IL:
      return msg(country_countries_IL);
    case Country.IS:
      return msg(country_countries_IS);
    case Country.IT:
      return msg(country_countries_IT);
    case Country.JE:
      return msg(country_countries_JE);
    case Country.JP:
      return msg(country_countries_JP);
    case Country.KR:
      return msg(country_countries_KR);
    case Country.KZ:
      return msg(country_countries_KZ);
    case Country.LT:
      return msg(country_countries_LT);
    case Country.LU:
      return msg(country_countries_LU);
    case Country.LV:
      return msg(country_countries_LV);
    case Country.MA:
      return msg(country_countries_MA);
    case Country.MC:
      return msg(country_countries_MC);
    case Country.MD:
      return msg(country_countries_MD);
    case Country.ME:
      return msg(country_countries_ME);
    case Country.MK:
      return msg(country_countries_MK);
    case Country.MT:
      return msg(country_countries_MT);
    case Country.NL:
      return msg(country_countries_NL);
    case Country.NO:
      return msg(country_countries_NO);
    case Country.NZ:
      return msg(country_countries_NZ);
    case Country.PH:
      return msg(country_countries_PH);
    case Country.PL:
      return msg(country_countries_PL);
    case Country.PT:
      return msg(country_countries_PT);
    case Country.RE:
      return msg(country_countries_RE);
    case Country.RO:
      return msg(country_countries_RO);
    case Country.RS:
      return msg(country_countries_RS);
    case Country.RU:
      return msg(country_countries_RU);
    case Country.SE:
      return msg(country_countries_SE);
    case Country.SG:
      return msg(country_countries_SG);
    case Country.SI:
      return msg(country_countries_SI);
    case Country.SK:
      return msg(country_countries_SK);
    case Country.TN:
      return msg(country_countries_TN);
    case Country.TR:
      return msg(country_countries_TR);
    case Country.TW:
      return msg(country_countries_TW);
    case Country.UK:
      return msg(country_countries_UK);
    case Country.US:
      return msg(country_countries_US);
    case Country.ZA:
      return msg(country_countries_ZA);
  }

  return exhaustiveGuard(
    country,
    `Cannot get the display name for country "${country}": ` +
      'The country does not have a configured display name. ' +
      'Did you update the `countries` lang file ' +
      'and the mapping in `getCountryName`?'
  );
};

/**
 * Retrieves the display name for the specified language.
 *
 * **NOTE:** Names must be configured via the `language` lang file.
 *
 * @param language - The language to get the name for.
 * @returns The specified language's display name.
 *
 * @throws An {@link InvalidArgumentError} if the specified language does not
 * have a configured display name.
 */
export const getLanguageName = (language: Language): string => {
  switch (language) {
    case Language.EN:
      return msg(language_en);
    case Language.ES:
      return msg(language_es);
    case Language.FR:
      return msg(language_fr);
    case Language.DE:
      return msg(language_de);
    case Language.IT:
      return msg(language_it);
    case Language.JA:
      return msg(language_jp);
    case Language.NL:
      return msg(language_nl);
  }

  return exhaustiveGuard(
    language,
    `Cannot get the display name for language "${language}": ` +
      'The language does not have a configured display name. ' +
      'Did you update the `language` lang file ' +
      'and the mapping in `getLanguageName`?'
  );
};
